import React from 'react';
import ReactDOM from 'react-dom';

interface FullscreenMediaPopupProps {
  isOpen: boolean;
  src?: string;
  mediaType?: 'image' | 'video';
  onClose: () => void;
  closeLabel?: string;
  forceLandscape?: boolean;
  videoInitialTime?: number;
  videoInitialPaused?: boolean;
  onVideoStateChange?: (state: { currentTime: number; paused: boolean }) => void;
  disableVideoControls?: boolean;
  closeOnVideoEnd?: boolean;
  showReplayOverlay?: boolean;
}

function FullscreenMediaPopup({
  isOpen,
  src,
  mediaType,
  onClose,
  closeLabel = 'Zamknij',
  forceLandscape = false,
  videoInitialTime = 0,
  videoInitialPaused = false,
  onVideoStateChange,
  disableVideoControls = false,
  closeOnVideoEnd = false,
  showReplayOverlay = false,
}: FullscreenMediaPopupProps) {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const lastVideoStateRef = React.useRef<{ currentTime: number; paused: boolean }>({ currentTime: 0, paused: true });
  const [isVideoEnded, setIsVideoEnded] = React.useState(false);

  React.useEffect(() => {
    if (mediaType !== 'video' || !videoRef.current) {
      return;
    }

    const video = videoRef.current;
    video.preload = 'auto';
    video.load();
  }, [src, mediaType]);

  React.useEffect(() => {
    if (!isOpen || mediaType !== 'video' || !videoRef.current) {
      return;
    }

    setIsVideoEnded(false);

    const video = videoRef.current;

    const applyInitialState = async () => {
      try {
        video.currentTime = videoInitialTime || 0;
      } catch (error) {
        // Ignore seek errors for very short/unready media.
      }

      if (videoInitialPaused) {
        video.pause();
      } else {
        try {
          await video.play();
        } catch (error) {
          // Autoplay may be blocked by the browser.
        }
      }

      lastVideoStateRef.current = { currentTime: video.currentTime, paused: video.paused };
      onVideoStateChange?.({ currentTime: video.currentTime, paused: video.paused });
    };

    applyInitialState();
  }, [isOpen, mediaType, videoInitialTime, videoInitialPaused, onVideoStateChange]);

  const emitVideoState = React.useCallback(() => {
    if (!videoRef.current) {
      return;
    }

    lastVideoStateRef.current = {
      currentTime: videoRef.current.currentTime,
      paused: videoRef.current.paused,
    };
    onVideoStateChange?.({
      currentTime: videoRef.current.currentTime,
      paused: videoRef.current.paused,
    });
  }, [onVideoStateChange]);

  React.useEffect(() => {
    if (!isOpen || mediaType !== 'video') {
      return;
    }

    const restoreAfterResize = () => {
      const video = videoRef.current;
      if (!video) return;

      const { currentTime, paused } = lastVideoStateRef.current;

      const apply = async () => {
        try {
          video.currentTime = currentTime || 0;
        } catch (error) {
          // ignore seek errors on resize
        }

        if (!paused && !video.ended) {
          try {
            await video.play();
          } catch (error) {
            // autoplay may be blocked
          }
        }
      };

      setTimeout(() => {
        apply();
      }, 50);
    };

    window.addEventListener('orientationchange', restoreAfterResize);
    window.addEventListener('resize', restoreAfterResize);

    return () => {
      window.removeEventListener('orientationchange', restoreAfterResize);
      window.removeEventListener('resize', restoreAfterResize);
    };
  }, [isOpen, mediaType]);

  if (!src || !mediaType) {
    return null;
  }

  const handleReplay = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      videoRef.current.play().catch(() => {});
      setIsVideoEnded(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className={`fullscreen-media-popup ${isOpen ? 'fullscreen-media-popup--open' : ''} ${forceLandscape ? 'fullscreen-media-popup--force-landscape' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Podglad multimedia">
      <div className="fullscreen-media-popup__overlay" onClick={onClose} />
      <div className="fullscreen-media-popup__content">
        <button type="button" className="fullscreen-media-popup__close" onClick={onClose}>
          {closeLabel}
        </button>
        <div className="fullscreen-media-popup__media-wrapper">
          {mediaType === 'video' ? (
            <>
              <video
                ref={videoRef}
                className={`fullscreen-media-popup__media ${disableVideoControls ? 'fullscreen-media-popup__media--locked' : ''}`}
                src={src}
                controls={!disableVideoControls}
                autoPlay={false}
                muted
                playsInline
                preload="auto"
                onTimeUpdate={emitVideoState}
                onPlay={emitVideoState}
                onPause={emitVideoState}
                onSeeked={emitVideoState}
                onLoadedData={emitVideoState}
                onEnded={() => {
                  emitVideoState();
                  if (closeOnVideoEnd && !showReplayOverlay) {
                    onClose();
                  } else if (showReplayOverlay) {
                    setIsVideoEnded(true);
                  }
                }}>
                Twoja przegladarka nie obsluguje elementu video.
              </video>
              {mediaType === 'video' && showReplayOverlay && isVideoEnded && (
                <button type="button" className="fullscreen-media-popup__replay-overlay" onClick={handleReplay} aria-label="Odtwórz ponownie">
                  <svg className="fullscreen-media-popup__replay-icon" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.45)" />
                    <path d="M24 18L46 32L24 46V18Z" fill="white" />
                  </svg>
                </button>
              )}
            </>
          ) : (
            <img className="fullscreen-media-popup__media" src={src} alt="" />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default FullscreenMediaPopup;
